import { ProtectedRoute } from '@skaleminds/jig-auth-sdk-ui';
import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminReportRedirect } from '@/components/AdminReportRedirect';
import { AdminRoute } from '@/components/AdminRoute';
import { AppShell } from '@/components/layout/AppShell';
import { NAV_ITEMS, type NavItem } from '@/components/layout/nav-items';
import { OversightRoute } from '@/components/OversightRoute';
import { ReturnToRedirect } from '@/components/ReturnToRedirect';
import { SharedLinkGate } from '@/components/SharedLinkGate';
import { Skeleton } from '@/components/ui/skeleton';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { SharedLinksPage } from '@/pages/admin/links/SharedLinksPage';
import { RecordsPage } from '@/pages/admin/records/RecordsPage';
import { GlobalSettingsPage } from '@/pages/admin/settings/GlobalSettingsPage';
import { TemplateDetailPage } from '@/pages/admin/templates/TemplateDetailPage';
import { TemplatesPage } from '@/pages/admin/templates/TemplatesPage';
import { TrackerPage } from '@/pages/admin/tracker/TrackerPage';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { LoginRoute } from '@/pages/LoginRoute';
import { TimeLogPage } from '@/pages/log/TimeLogPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OversightNotesPage } from '@/pages/oversight/OversightNotesPage';
import { OversightRecordsPage } from '@/pages/oversight/OversightRecordsPage';
import { OversightTrackerPage } from '@/pages/oversight/OversightTrackerPage';
import { ProjectDetailPage } from '@/pages/projects/ProjectDetailPage';
import { ProjectsPage } from '@/pages/projects/ProjectsPage';
import { SettingsPage } from '@/pages/settings/SettingsPage';
import { UsersPage } from '@/pages/users/UsersPage';

// Pages with recharts load on demand so the entry chunk stays under 500 kB (sprint 4 §6.5, sprint 5 §6.1).
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const SharedReportPage = lazy(() => import('@/pages/shared/SharedReportPage').then((m) => ({ default: m.SharedReportPage })));
// Sprint 13: My shifts and the Supervisor Attendance page load on demand (§15).
const MyShiftsPage = lazy(() => import('@/pages/shifts/MyShiftsPage').then((m) => ({ default: m.MyShiftsPage })));
const OversightAttendancePage = lazy(() => import('@/pages/oversight/OversightShiftPages').then((m) => ({ default: m.OversightAttendancePage })));
// Sprint 14: the check-in alarm SOP page, linked from the shift card and the phone warning (§8.4).
// Sprint 15: My notes (self-notes) loads on demand (§9.4).
const MyNotesPage = lazy(() => import('@/pages/notes/MyNotesPage').then((m) => ({ default: m.MyNotesPage })));
const BeforeYourShiftPage = lazy(() => import('@/pages/shifts/BeforeYourShiftPage').then((m) => ({ default: m.BeforeYourShiftPage })));

function lazyPage(page: ReactNode): ReactNode {
  return <Suspense
      fallback={
        <div className="p-4 sm:p-6">
          <Skeleton className="h-96 w-full" />
        </div>
      }
    >{page}</Suspense>;
}

/** Built pages by nav key. */
function pageFor(item: NavItem): ReactNode {
  switch (item.key) {
    case 'dashboard':
      return <DashboardPage />;
    case 'reports':
      return lazyPage(<ReportsPage />);
    case 'settings':
      return <SettingsPage />;
    case 'log':
      return <TimeLogPage />;
    case 'projects':
      return <ProjectsPage />;
    case 'users':
      return <UsersPage />;
    case 'records':
      return <RecordsPage />;
    case 'tracker':
      return <TrackerPage />;
    case 'links':
      return <SharedLinksPage />;
    case 'global-settings':
      return <GlobalSettingsPage />;
    case 'templates':
      return <TemplatesPage />;
    case 'oversight-records':
      return <OversightRecordsPage />;
    case 'oversight-tracker':
      return <OversightTrackerPage />;
    case 'oversight-notes':
      return <OversightNotesPage />;
    case 'shifts':
      return lazyPage(<MyShiftsPage />);
    case 'my-notes':
      return lazyPage(<MyNotesPage />);
    case 'oversight-attendance':
      return lazyPage(<OversightAttendancePage />);
    default:
      return <ComingSoonPage title={item.label} />;
  }
}

/** Sprint 19: the roster is gone; an old /oversight/roster bookmark lands on Attendance with its ?project= kept. */
export function OversightRosterRedirect(): JSX.Element {
  const { search } = useLocation();
  return <Navigate replace to={{ pathname: '/oversight/attendance', search }} />;
}

function guarded(item: NavItem, page: ReactNode): ReactNode {
  if (item.visibility === 'admin') return <AdminRoute>{page}</AdminRoute>;
  if (item.visibility === 'oversight') return <OversightRoute>{page}</OversightRoute>;
  if (item.visibility === 'notes') return <OversightRoute need="notes">{page}</OversightRoute>;
  if (item.visibility === 'attendance') return <OversightRoute need="attendance">{page}</OversightRoute>;
  return page;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Public: outside ProtectedRoute and the AppShell (architecture sprint-02 §10.0). */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      {/* Sprint 5: signed-in share view inside the app shell; own gate so the return path survives sign-in. */}
      <Route
        path="/shared/:token"
        element={
          <SharedLinkGate>
            <AppShell />
          </SharedLinkGate>
        }
      >
        <Route index element={lazyPage(<SharedReportPage />)} />
      </Route>
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <ReturnToRedirect />
            <AppShell />
          </ProtectedRoute>
        }
      >
        {NAV_ITEMS.map((item) => {
          const element = guarded(item, pageFor(item));
          return item.path === '/' ? (
            <Route key={item.key} index element={element} />
          ) : (
            <Route key={item.key} path={item.path.slice(1)} element={element} />
          );
        })}
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="shifts/before-your-shift" element={lazyPage(<BeforeYourShiftPage />)} />
        <Route
          path="admin/templates/:templateId"
          element={
            <AdminRoute>
              <TemplateDetailPage />
            </AdminRoute>
          }
        />
        {/* Sprint 11 redirects (F9, A-13). */}
        <Route path="admin/report" element={<AdminReportRedirect />} />
        <Route path="admin/team-settings" element={<Navigate replace to="/admin/settings" />} />
        {/* Sprint 19: unguarded, because the target page is guarded. */}
        <Route path="oversight/roster" element={<OversightRosterRedirect />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
